import { cn } from "./cn";

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** The text beside the switch; also its accessible name. */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * An on/off toggle: a pill with a knob, and a label.
 *
 * For a setting that is a mode rather than a selection — "only issued
 * invoices count" — where a checkbox reads as one item in a list and a
 * segmented control implies alternatives that have names. The whole thing is
 * one button, so the label is a click target and a screen reader gets one
 * control with one name and one state.
 */
export function Switch({ checked, onChange, label, disabled, className }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex items-center gap-2 text-[12px] text-ink-muted transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:text-ink",
        checked && "text-ink",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-block h-[18px] w-8 shrink-0 rounded-full transition-colors",
          checked ? "bg-accent" : "bg-line",
        )}
      >
        <span
          className={cn(
            "absolute top-[2px] size-[14px] rounded-full bg-surface shadow-sm transition-[left]",
            checked ? "left-[16px]" : "left-[2px]",
          )}
        />
      </span>
      {label}
    </button>
  );
}
